/**
 * Definition of the ModuleParam class.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */
import { TextUtil } from '@amplify3/duplo-back-end'

export enum ModuleParamType {
  Button,
  Slider,
  Number,
  StepSlider,
  Duration,
  AutoComplete,
  SwitchGroup,
}

export type ModuleParamOption = {
  value: any
  title: string
  defaultValue?: boolean
  name?: string
}

export class ModuleParam<T> {
  name: string
  title: string
  type: ModuleParamType
  id: string
  options?: ModuleParamOption[]
  tip?: string
  tip2?: string | JSX.Element
  min?: number
  max?: number
  step?: number
  defaultValue?: any
  unit?: string
  values?: any[]
  placeholder?: string
  leftIcon?: 'search'
  mandatory?: boolean

  constructor({
    name,
    title,
    type,
    options,
    tip,
    tip2,
    min,
    max,
    step,
    defaultValue,
    unit,
    values,
    placeholder,
    leftIcon,
    mandatory,
  }: {
    name: string
    title: string
    type: ModuleParamType
    options?: {
      value: T
      title: string
      defaultValue?: boolean
      name?: string
    }[]
    tip?: string
    tip2?: string | JSX.Element
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    unit?: string
    values?: any[]
    placeholder?: string
    leftIcon?: 'search'
    mandatory?: boolean
  }) {
    this.name = name
    this.title = title
    this.type = type
    this.id = this.getComponentId()
    this.options = options
    this.tip = tip
    this.tip2 = tip2
    this.min = min
    this.max = max
    this.step = step
    this.defaultValue = defaultValue
    this.unit = unit
    this.values = values
    this.placeholder = placeholder
    this.leftIcon = leftIcon
    this.mandatory = mandatory
  }

  getComponentId(suffix: string = '') {
    const capitalSuffix =
      TextUtil.capitalizeFirstChar(TextUtil.replaceSpaces(suffix)) ?? ''

    switch (this.type) {
      case ModuleParamType.Button:
        return TextUtil.replaceSpaces(
          'ParamButton' +
            TextUtil.capitalizeFirstChar(this.name) +
            TextUtil.capitalizeFirstChar(this.title) +
            capitalSuffix
        )

      case ModuleParamType.Slider:
        return TextUtil.replaceSpaces(
          'ParamSlider' +
            TextUtil.capitalizeFirstChar(this.name) +
            capitalSuffix
        )

      case ModuleParamType.Number:
        return TextUtil.replaceSpaces(
          'Param' + TextUtil.capitalizeFirstChar(this.name) + capitalSuffix
        )

      case ModuleParamType.StepSlider:
        return TextUtil.replaceSpaces(
          'ParamStepSlider' +
            TextUtil.capitalizeFirstChar(this.name) +
            capitalSuffix
        )

      case ModuleParamType.Duration:
        return TextUtil.replaceSpaces(
          'ParamDuration' +
            TextUtil.capitalizeFirstChar(this.name) +
            capitalSuffix
        )

      case ModuleParamType.AutoComplete:
        return TextUtil.replaceSpaces(
          'ParamAutoComplete' +
            TextUtil.capitalizeFirstChar(this.name) +
            capitalSuffix
        )

      case ModuleParamType.SwitchGroup:
        return TextUtil.replaceSpaces(
          'ParamSwitch' +
            TextUtil.capitalizeFirstChar(this.name) +
            capitalSuffix
        )
    }
  }
}
